import {useEffect, useState} from 'react';
import {isoDateToMomentNonUtc} from '../../forecast-app/shared/util/DateUtil';
import {AI_GATEWAY_URL} from './aiGateway';
import {subscribeAiCreditsExhausted} from './aiCreditsSignal';

/**
 * The account's AI credit balance for the current period, as the gateway reports it.
 * Only ever built from a response whose balance is known; anything else is represented as null.
 */
export type AiCreditUsage = {
	usedCredits: number;
	grantedCredits: number;
	remainingCredits: number;
	percentUsed: number;
	period: string;
	periodEnd: string;
	isTrial: boolean;
	enforced: boolean;
};

export const FOCUS_REFETCH_INTERVAL_MS = 5 * 60 * 1000;

const USAGE_URL = `${AI_GATEWAY_URL}/credits/usage`;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const parseAiCreditUsage = (body: unknown): AiCreditUsage | null => {
	if (!body || typeof body !== 'object') return null;
	const data = body as Record<string, unknown>;
	if (data.known !== true) return null;

	const {used_credits, granted_credits, remaining_credits, percent_used, period, period_end, is_trial, enforced} = data;
	if (
		!isFiniteNumber(used_credits) ||
		!isFiniteNumber(granted_credits) ||
		!isFiniteNumber(remaining_credits) ||
		!isFiniteNumber(percent_used)
	) {
		return null;
	}
	if (typeof period !== 'string' || typeof period_end !== 'string' || typeof enforced !== 'boolean') return null;

	return {
		usedCredits: used_credits,
		grantedCredits: granted_credits,
		remainingCredits: remaining_credits,
		percentUsed: percent_used,
		period,
		periodEnd: period_end,
		isTrial: is_trial === true,
		enforced,
	};
};

/**
 * The reset date as DD/MM/YYYY. period_end is a calendar date, so it is read in local time;
 * read as a UTC instant it would show the previous day anywhere west of UTC.
 */
export const formatCreditResetDate = (periodEnd: string): string | null => {
	if (!ISO_DATE.test(periodEnd)) return null;
	const date = isoDateToMomentNonUtc(periodEnd);
	return date && date.isValid() ? date.format('DD/MM/YYYY') : null;
};

type Subscriber = (usage: AiCreditUsage | null) => void;

// One store for the whole app, so the banners and the admin counter share a single request.
// It lives only while something subscribes; the last unsubscribe (sign-out unmounts the app
// shell) drops the balance so the next account never sees the previous one's numbers.
const subscribers = new Set<Subscriber>();
let current: AiCreditUsage | null = null;
let lastFetchStartedAt: number | null = null;
let inFlight = false;
let refetchQueued = false;
let generation = 0;
let detach: (() => void) | null = null;

const publish = (next: AiCreditUsage | null): void => {
	current = next;
	subscribers.forEach(subscriber => subscriber(next));
};

// Resolves to the new balance, to null when the gateway answered that the balance is unknown,
// or to undefined when there is no usable answer at all (network error, non-200, malformed body).
// Undefined keeps whatever is already shown: a transient failure must not hide a spent allowance.
const requestUsage = (): Promise<AiCreditUsage | null | undefined> => {
	if (typeof fetch !== 'function') return Promise.resolve(undefined);
	try {
		return fetch(USAGE_URL, {credentials: 'include', headers: {Accept: 'application/json'}})
			.then(response => (response.status === 200 ? response.json() : undefined))
			.then(body => {
				if (body && typeof body === 'object' && (body as Record<string, unknown>).known === false) return null;
				return parseAiCreditUsage(body) ?? undefined;
			})
			.catch(() => undefined);
	} catch {
		return Promise.resolve(undefined);
	}
};

const load = (): void => {
	if (inFlight) {
		// A response already on its way may predate whatever prompted this call.
		refetchQueued = true;
		return;
	}
	inFlight = true;
	lastFetchStartedAt = Date.now();
	const startedIn = generation;

	requestUsage().then(next => {
		if (startedIn !== generation) return;
		inFlight = false;
		if (next !== undefined) publish(next);
		if (refetchQueued) {
			refetchQueued = false;
			load();
		}
	});
};

const onFocus = (): void => {
	if (lastFetchStartedAt === null || Date.now() - lastFetchStartedAt >= FOCUS_REFETCH_INTERVAL_MS) {
		load();
	}
};

const attach = (): void => {
	window.addEventListener('focus', onFocus);
	const unsubscribeExhausted = subscribeAiCreditsExhausted(load);
	detach = () => {
		window.removeEventListener('focus', onFocus);
		unsubscribeExhausted();
	};
	load();
};

const reset = (): void => {
	if (detach) detach();
	detach = null;
	generation += 1;
	current = null;
	lastFetchStartedAt = null;
	inFlight = false;
	refetchQueued = false;
};

const subscribe = (subscriber: Subscriber): (() => void) => {
	subscribers.add(subscriber);
	if (subscribers.size === 1) attach();
	return () => {
		subscribers.delete(subscriber);
		if (subscribers.size === 0) reset();
	};
};

/**
 * The account's AI credit balance, or null when it is unknown, still loading, or could not be
 * fetched. Fetches after the first render, never during it, so a page never waits on the gateway.
 * Refetches on window focus at most once per FOCUS_REFETCH_INTERVAL_MS, and at once whenever the
 * chat is refused for a spent allowance.
 */
export const useAiCreditUsage = (enabled = true): AiCreditUsage | null => {
	const [usage, setUsage] = useState<AiCreditUsage | null>(current);

	useEffect(() => {
		if (!enabled) return;
		setUsage(current);
		return subscribe(setUsage);
	}, [enabled]);

	return enabled ? usage : null;
};
